import { lazy, Suspense, useCallback, useState } from "react";

import Loading from "components/Loading";
import { INIT_ACCOUNT, INIT_DEPARTMENT } from "constants/filter";
import { orderDetailDisplayPage } from "constants/orderDetailDisplayPage";
import { SortDirection, sortDirection } from "constants/sortDirection";
import useFindOrders from "hooks/api/useFindOrders";
import useGetOrder, { useInvalidateOrderCache } from "hooks/api/useGetOrder";
import useGetUsers from "hooks/api/useGetUsers";
import useStopSearchProperty from "hooks/api/useStopSearchProperty";
import { useSyncEffect } from "hooks/effects";
import { useDebounce } from "hooks/useDebounce";
import useErrorModalStatusContext from "hooks/useErrorModalStatusContext";
import { hasSavedGlobalFilter, useGlobalDepartmentAccountFilter } from "hooks/useGlobalDepartmentAccountFilter";
import { useGoogleAnalytics } from "hooks/useGoogleAnalytics";
import usePageNumberSearchParam from "hooks/usePageNumberSearchParam";
import usePageTransition from "hooks/usePageTransition";
import { OrderSortKey, orderSortKey } from "pages/OrderList/orderSortKey";
import { OrderStatusFilter } from "pages/OrderList/orderStatusFilter";
import { findUniqueDepartments } from "utils/accountUtils";
import { isBrowser, isTablet } from "utils/deviceDetect";
import { findFromLocalStorage, saveToLocalStorage } from "utils/localStorageUtils";

const IS_KEEP_ORDER_FILTER_KEY = "keep-orders-status";

const PAGE_SIZE = 20;

export type YearAndMonth = {
  year: string;
  month: string;
};

const OrderList = lazy(() =>
  isBrowser
    ? import("pages/OrderList/pc/OrderList")
    : isTablet
      ? import("pages/OrderList/tb/OrderList")
      : import("pages/OrderList/sp/OrderList")
);

const OrderListContainer = () => {
  const queryParameter = new URLSearchParams(window.location.search);

  const [searchKeyword, setSearchKeyword] = useState<string | undefined>();
  const { debouncedValue: debouncedSearchKeyword } = useDebounce(searchKeyword, 500);

  const [orderStatusFilter, setOrderStatusFilter] = useState<OrderStatusFilter | undefined>(
    (queryParameter.get("filter") as OrderStatusFilter) ?? OrderStatusFilter.all
  );
  const [orderCreatedAtSortOption, setOrderCreatedAtSortOption] = useState<SortDirection>(sortDirection.desc);
  const [searchStatusSortOption, setSearchStatusSortOption] = useState<SortDirection>(sortDirection.asc);
  const [sortKey, setSortKey] = useState<OrderSortKey>(orderSortKey.createdAt);

  const [currentPageNumber, setCurrentPageNumber] = usePageNumberSearchParam();

  const { accountList } = useGetUsers();
  const { chosenDepartment, chosenAccount, setChosenDepartment, setChosenAccount, filteredAccountList, isFilterReady } =
    useGlobalDepartmentAccountFilter(accountList ?? []);
  const departmentList = findUniqueDepartments(accountList);
  const { stopSearchProperty } = useStopSearchProperty();

  const [orderId, setOrderId] = useState("");
  const [isVisible, setIsVisible] = useState(false);

  const { sendEvent } = useGoogleAnalytics(true);
  const onClickSortCreatedAt = useCallback(
    (direction: SortDirection) => {
      setSortKey(orderSortKey.createdAt);
      setOrderCreatedAtSortOption(direction);
      sendEvent({
        action: "依頼検索_依頼日_ソート",
        category: "sort",
        label: location.pathname.replace(/\/\w{18}($|\/)/, "/detail$1"),
      });
    },
    [sendEvent]
  );
  const onClickSortSearchStatus = useCallback(
    (direction: SortDirection) => {
      setSortKey(orderSortKey.searchStatus);
      setSearchStatusSortOption(direction);
      sendEvent({
        action: "依頼検索_検索ステータス_ソート",
        category: "sort",
        label: location.pathname.replace(/\/\w{18}($|\/)/, "/detail$1"),
      });
    },
    [sendEvent]
  );

  const {
    orders: orderList,
    pagination,
    error: ordersError,
    mutate: findOrdersMutate,
  } = useFindOrders(
    isFilterReady
      ? {
          pageSize: PAGE_SIZE,
          pageNumber: currentPageNumber,
          orderType: sortKey === orderSortKey.searchStatus ? searchStatusSortOption : orderCreatedAtSortOption,
          sortKey,
          searchKeyword: debouncedSearchKeyword,
          contactId: chosenAccount?.id !== INIT_ACCOUNT.id ? chosenAccount?.id : undefined,
          department: chosenDepartment !== INIT_DEPARTMENT ? chosenDepartment : undefined,
          orderStatusFilter: orderStatusFilter ?? findFromLocalStorage(IS_KEEP_ORDER_FILTER_KEY, OrderStatusFilter.all),
        }
      : null
  );

  const navigate = usePageTransition();

  const { order, error: orderDetailError, mutate: getOrderMutate } = useGetOrder(orderId);
  const invalidateOrderCache = useInvalidateOrderCache();

  const { showErrorModal } = useErrorModalStatusContext();

  const onClickStopSearchButton = async (targetOrderId: string, onSuccess?: () => void) => {
    const statusCode = await stopSearchProperty(targetOrderId || null).catch(() => undefined);
    // A non-200 answer did not switch the search either, so it gets the same error as a thrown request.
    if (statusCode !== 200) {
      showErrorModal({
        httpMethod: "put",
      });
      return;
    }
    // The estimate detail reads the order from cache, so drop it there or it keeps the old status.
    await invalidateOrderCache(targetOrderId);
    onSuccess?.();
    findOrdersMutate();
    getOrderMutate();
  };

  const onClickOrderRow = async (orderId: string) => {
    sendEvent({
      action: "工事詳細を開く_エリア",
      category: "other",
      label: "/order",
    });
    setIsVisible(true);
    setOrderId(orderId);
  };
  const onDrawerClose = () => {
    setIsVisible(false);
    setOrderId("");
  };

  useSyncEffect(() => {
    if (ordersError || orderDetailError) {
      showErrorModal({ httpMethod: "get" });
    }
  }, [orderDetailError, ordersError, showErrorModal]);

  const toOrderDetail = (orderId: string): void => {
    sendEvent({
      action: "見積画面に遷移_ボタン",
      category: "button",
      label: "/order",
    });
    navigate(`/order/${orderId}`);
  };
  const toEditOrder = (orderId: string): void => {
    sendEvent({
      action: "依頼変更に遷移_リンク",
      category: "link",
      label: "/order",
    });
    navigate(`/order/${orderId}`, { state: { redirectDisplayPage: orderDetailDisplayPage.EDIT_ORDER } });
  };
  const toContract = (orderId: string): void => {
    sendEvent({
      action: "契約画面に遷移_ボタン",
      category: "button",
      label: "/order",
    });
    navigate(`/order/${orderId}/contract`);
  };
  const toBillingList = (yearAndMonth: YearAndMonth | undefined) => {
    sendEvent({
      action: "契約画面に遷移_ボタン",
      category: "button",
      label: "/order",
    });
    navigate(yearAndMonth ? `/billing?year=${yearAndMonth.year}&month=${yearAndMonth.month}` : `/billing`);
  };
  return (
    <Suspense fallback={<Loading />}>
      <OrderList
        isFirstFetch={
          isFilterReady &&
          searchKeyword == null &&
          chosenAccount == null &&
          chosenDepartment == null &&
          !hasSavedGlobalFilter() &&
          orderStatusFilter == null &&
          // usageページでリロード時に必要。一度でもfetch成功していなければ、all以外にできない。allのままならfirstFetchの可能性有とみなす
          findFromLocalStorage(IS_KEEP_ORDER_FILTER_KEY, OrderStatusFilter.all) === OrderStatusFilter.all
        }
        orderList={orderList}
        searchKeyword={searchKeyword ?? ""}
        updateSearchKeyword={setSearchKeyword}
        orderStatusFilter={orderStatusFilter ?? findFromLocalStorage(IS_KEEP_ORDER_FILTER_KEY, OrderStatusFilter.all)}
        setOrderStatusFilter={setOrderStatusFilter}
        setLocalStorage={(status: OrderStatusFilter) => saveToLocalStorage(IS_KEEP_ORDER_FILTER_KEY, status)}
        orderCreatedAtSortDirection={orderCreatedAtSortOption}
        updateOrderCreatedAtSortDirection={onClickSortCreatedAt}
        searchStatusSortDirection={searchStatusSortOption}
        updateSearchStatusSortDirection={onClickSortSearchStatus}
        sortKey={sortKey}
        accountList={accountList ?? []}
        filteredAccountList={filteredAccountList}
        chosenAccount={chosenAccount}
        setChosenAccount={setChosenAccount}
        departmentList={departmentList ?? []}
        chosenDepartment={chosenDepartment}
        setChosenDepartment={setChosenDepartment}
        totalPageCount={pagination.totalPages ?? 1}
        currentPageNumber={currentPageNumber}
        setCurrentPageNumber={setCurrentPageNumber}
        isVisible={isVisible}
        constructionDisplayInDetail={order}
        onClickOrderRow={onClickOrderRow}
        onClickOrderStatus={toOrderDetail}
        onClickContractStatus={toContract}
        onClickBillingStatus={toBillingList}
        onCloseDetail={onDrawerClose}
        onClickConstructionEditButton={toEditOrder}
        onClickStopSearchButton={onClickStopSearchButton}
      />
    </Suspense>
  );
};

export default OrderListContainer;
